import OpenAI from 'openai';
import { getInstanceId, performWithIamToken } from 'scrivito';

import { DEFAULT_MODEL } from './config';

function genAiServiceUrl(
  baseUrl = 'https://api.justrelate.com/gen-ai/',
  instanceId?: string,
): string {
  const path = `instances/${instanceId ?? getInstanceId()}/v1`;
  return new URL(path, baseUrl).toString();
}

export function createClient({
  useAnonymously = false,
  baseUrl,
  instanceId,
  maxRetries,
  defaultHeaders,
}: {
  useAnonymously?: boolean;
  baseUrl?: string;
  instanceId?: string;
  maxRetries?: number;
  defaultHeaders?: Record<string, string>;
} = {}) {
  return new OpenAI({
    // note: this is a fake key, which is not used
    // we overwrite the authorization header in our fetch implementation
    apiKey: 'dont-use-this-key',
    baseURL: genAiServiceUrl(baseUrl, instanceId),
    defaultHeaders: { Accept: '*/*', ...defaultHeaders },
    dangerouslyAllowBrowser: true,
    maxRetries,

    fetch: async (url, init) => {
      const headers = prepareHeaders(init);
      if (useAnonymously) return fetch(url, { ...init, headers });

      return performWithIamToken<Response>(
        'https://api.justrelate.com/gen-ai',
        async (token) => {
          headers.set('authorization', `Bearer ${token}`);

          const result = await fetch(url, { ...init, headers });

          if (result.status === 401) {
            return { authenticationFailed: await result.json() };
          }

          return { result };
        },
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore while SDK 1.64 is not yet released. TODO: drop afterwards
        { authViaInstance: instanceId },
      );
    },
  });
}

function prepareHeaders(init: RequestInit | undefined): Headers {
  const headers = new Headers(init?.headers);

  for (const key of [...headers.keys()]) {
    // Remove "junk" headers that our endpoint doesn't like
    if (key.toLowerCase().startsWith('x-')) headers.delete(key);
  }

  headers.delete('authorization');
  headers.set('content-type', 'application/json');

  return headers;
}

async function runTest() {
  const client = createClient();

  const stream = await client.chat.completions.create({
    model: DEFAULT_MODEL,
    messages: [
      {
        role: 'user',
        content: 'Please tell me who you are.',
      },
    ],
    stream: true,
  });
  for await (const chunk of stream) {
    // eslint-disable-next-line no-console
    console.log(chunk.choices[0]?.delta?.content || '');
  }
}

if (typeof window !== 'undefined') {
  // @ts-expect-error - expose runTest to the window
  window.runTest = runTest;
}
